import { isApiError } from "@pokifolio/api-client";
import type { ResearchEvent, ResearchListing } from "@pokifolio/contracts";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink } from "lucide-react";

import { ErrorAlert } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { useCurrentUser } from "../auth/session";
import { ConditionCheck } from "./condition-check";
import { DeleteResearchButton } from "./delete-research-button";
import { Fact } from "./fact";
import { ApprovalSection } from "./approval";
import { PriceCheck } from "./price-check";
import {
  MATCH_LABELS,
  MATCH_TONES,
  WEAR_LABELS,
  describeAvailability,
  formatDateTime,
  formatMoney,
  isWearWarning,
  listingTypeLabel,
} from "../search/format";
import { EVENT_LABELS, RESEARCH_STATUS_LABELS, RESEARCH_STATUS_TONES, formatDate } from "./format";
import { useResearchRecord } from "./research-queries";

const backLink = (
  <Link to="/research" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
    <ArrowLeft className="size-4" aria-hidden="true" />
    All research
  </Link>
);

/** One saved listing: the card, the listing exactly as it was saved, and every step so far. */
export function ResearchDetailPage({ researchId }: { researchId: string }) {
  const record = useResearchRecord(researchId);
  const { user } = useCurrentUser();

  if (record.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {backLink}
        <Card>
          <p className="text-sm text-muted-foreground">Loading research…</p>
        </Card>
      </div>
    );
  }
  if (record.isError) {
    const notFound = isApiError(record.error) && (record.error.status === 404 || record.error.status === 422);
    return (
      <div className="flex flex-col gap-4">
        {backLink}
        {notFound ? (
          <Card>
            <h1 className="text-lg font-semibold text-foreground">This research was not found.</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              It may have been opened from an old or mistyped link.
            </p>
          </Card>
        ) : (
          <ErrorAlert error={record.error} />
        )}
      </div>
    );
  }

  const { research, history } = record.data;
  const { card, listing } = research;
  const picture = card.imageLarge ?? card.imageSmall;
  const savedBy = research.owner.id === user.id ? "you" : research.owner.name;

  return (
    <div className="flex flex-col gap-6">
      {backLink}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {card.name} {card.number}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{card.setName}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Saved by {savedBy} on {formatDate(research.savedAt)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge tone={RESEARCH_STATUS_TONES[research.status]}>
            {RESEARCH_STATUS_LABELS[research.status]}
          </Badge>
          <DeleteResearchButton research={research} />
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-[220px_1fr]">
        <Card className="flex items-start justify-center">
          {picture === null ? (
            <div className="flex aspect-[5/7] w-full items-center justify-center rounded bg-muted text-xs text-muted-foreground">
              No picture
            </div>
          ) : (
            <img
              src={picture}
              alt={`${card.name} ${card.number}`}
              className="w-full rounded object-contain"
            />
          )}
        </Card>

        <div className="flex flex-col gap-6">
          <ListingSection listing={listing} />
          <ConditionCheck research={research} />
          <PriceCheck research={research} />
          <ApprovalSection research={research} />
          <History events={history} currentUserId={user.id} />
        </div>
      </div>
    </div>
  );
}

function ListingSection({ listing }: { listing: ResearchListing }) {
  const availability = describeAvailability(listing.availability);
  const type = listingTypeLabel(listing.listingType);
  return (
    <Card>
      <section aria-labelledby="listing-heading" className="flex flex-col gap-4">
        <div>
          <h2 id="listing-heading" className="text-base font-semibold text-foreground">
            Listing, as saved
          </h2>
          <a
            href={listing.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-start gap-1 text-sm font-medium text-primary hover:underline"
          >
            {listing.title}
            <ExternalLink className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        </div>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <Fact label="Website">{listing.website}</Fact>
          <Fact label="Price">
            {listing.price === null ? (
              <span className="text-muted-foreground">Not known; see website</span>
            ) : (
              <>
                {listing.listingType === "auction" ? "Bid " : ""}
                {formatMoney(listing.price)}
                {listing.priceConfidence === "hint" && (
                  <span className="block text-xs text-amber-800 dark:text-amber-300">
                    From search results; may be out of date
                  </span>
                )}
              </>
            )}
          </Fact>
          <Fact label="Availability">
            <Badge tone={availability.tone}>{availability.label}</Badge>
            {availability.note !== null && (
              <span className="block text-xs text-muted-foreground">{availability.note}</span>
            )}
          </Fact>
          <Fact label="Type and seller’s condition">
            {[type, listing.condition].filter((v) => v !== null).join(" · ") || "Not given"}
            {listing.wear !== null && (
              <span
                className={
                  isWearWarning(listing.wear)
                    ? "block text-xs font-medium text-amber-800 dark:text-amber-300"
                    : "block text-xs text-muted-foreground"
                }
              >
                {isWearWarning(listing.wear) ? "⚠ " : ""}Condition: {WEAR_LABELS[listing.wear]}
              </span>
            )}
          </Fact>
          <Fact label="Match with this card">
            <Badge tone={MATCH_TONES[listing.match.level]}>{MATCH_LABELS[listing.match.level]}</Badge>
            <ul className="mt-1 flex flex-col text-xs text-muted-foreground">
              {listing.match.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </Fact>
        </dl>
        <p className="text-xs text-muted-foreground">
          As seen on {formatDateTime(listing.seenAt)}. The price and stock may have changed since; always
          check the website before buying.
        </p>
      </section>
    </Card>
  );
}

function History({ events, currentUserId }: { events: readonly ResearchEvent[]; currentUserId: string }) {
  return (
    <Card>
      <section aria-labelledby="history-heading" className="flex flex-col gap-2">
        <h2 id="history-heading" className="text-base font-semibold text-foreground">
          History
        </h2>
        <ol aria-label="History" className="flex flex-col gap-2 text-sm">
          {events.map((event) => (
            <li key={event.id} className="flex flex-wrap gap-x-2">
              <span className="font-medium text-foreground">{EVENT_LABELS[event.type]}</span>
              <span className="text-muted-foreground">
                by {event.actor.id === currentUserId ? "you" : event.actor.name} · {formatDateTime(event.at)}
                {event.maxPriceUsd !== null &&
                  ` · up to ${formatMoney({ amount: event.maxPriceUsd, currency: "USD" })}`}
              </span>
              {event.note !== null && <span className="w-full text-muted-foreground">“{event.note}”</span>}
            </li>
          ))}
        </ol>
      </section>
    </Card>
  );
}
